/**
 * Right-side drawer for project inspection (singleton — at most one mounted
 * into the Gantt container, parallel to task-inspector). Renders project form
 * in view-mode; create-mode renders a minimal form (name + goal + priority +
 * hard_deadline). Status transitions delegated to the parent (gantt.js)
 * through onAction.
 *
 * Usage:
 *   mountProjectInspector(container);
 *   openProjectInspector({ project, onAction, onClose });
 *   openCreateProjectInspector({ onAction, onClose });
 *   refreshInspectorProject(updatedProject);
 *   getProjectInspectorDiff();
 *   showProjectInspectorError(msg);
 *   closeProjectInspector();
 */

import { attachInspectorResize } from '../utils/inspector-resize.js';
import { api } from '../api.js';
import { iconFolder } from '../utils/icons.js';
import { canDo } from '../auth.js';
import { feverEmoji } from '../utils/format.js';
import { showNotification } from './notification.js';
import { createInlinePicker } from './inline-picker.js';

// Singleton picker для add-manager / add-viewer. document-listeners
// (pointerdown/keydown) регистрируются один раз — паттерн как в views/admin.js.
const _picker = createInlinePicker();
document.body.appendChild(_picker.element);

const STATUS_RU = {
  backlog:   'backlog',
  queued:    'queued',
  active:    'active',
  paused:    'paused',
  done:      'done',
  cancelled: 'cancelled',
};

const PRIORITIES = [
  { value: 'P0',      label: 'P0 (критично)' },
  { value: 'P1',      label: 'P1 (важно)' },
  { value: 'P2',      label: 'P2 (обычное, default)' },
  { value: 'P3',      label: 'P3 (фоном)' },
  { value: 'backlog', label: 'backlog (не активен)' },
];

let _root  = null;
let _state = resetState();

function resetState() {
  return {
    mode:       'closed',  // 'closed' | 'view' | 'create'
    projectId:  null,
    project:    null,
    onAction:   () => {},
    onClose:    () => {},
    errorMsg:   null,
    assignments: null,     // { managers: [User], viewers: [User] } после hydrate (Wave 2)
    // Create-flow: портфель определяет доступ (Wave 4 portfolio_visibility).
    // Заменил флаг is_business 2026-05-13 — он остался в БД как историческая метка,
    // но в форме создания больше не нужен (см. plans/2026-05-13-portfolio-instead-of-isbusiness.md).
    portfolioId: null,
    portfolios:  [],
    docs: {
      sostav_p: null,
      sostav_r: null,
      tz: null,
      contract: null,
      calendar_plan: null,
    },
  };
}

// Метаданные 5 слотов документов в порядке отображения.
// requireGroup='sostav' — OR-группа: хотя бы один файл из всех слотов
// этой группы (П или Р). assembler работает с любым из двух.
// required:true — индивидуально обязательный (ТЗ).
// Остальные слоты опциональны — можно создать с любым набором или без файлов.
const DOC_SLOTS = [
  { key: 'sostav_p',     label: 'Состав проекта (П)',  requireGroup: 'sostav' },
  { key: 'sostav_r',     label: 'Состав проекта (Р)',  requireGroup: 'sostav' },
  { key: 'tz',           label: 'Техническое задание', required: true         },
  { key: 'contract',     label: 'Контракт'                                    },
  { key: 'calendar_plan',label: 'Календарный план'                            },
];

// Человеко-читаемые названия групп для текстов ошибок.
const REQUIRE_GROUP_LABELS = {
  sostav: 'Состав проекта (П) или Состав проекта (Р)',
};

// Все редактируемые поля проекта. Для diff и readForm.
// Все строковые/date — без типизации NUMERIC/BOOL (portfolio_id — numeric, обрабатываем отдельно).
const FIELDS_ALL = [
  'name', 'project_code', 'goal', 'why_now',
  'hard_deadline', 'soft_deadline',
  'priority', 'contour', 'portfolio_id',
];

// Кэш портфелей (общий для всех инспекторов одной сессии). Загружается
// на первое открытие view-режима; не блокирует render().
let _portfoliosCache = null;
async function ensurePortfolios() {
  if (_portfoliosCache) return _portfoliosCache;
  try {
    _portfoliosCache = await api.portfolios();
  } catch {
    _portfoliosCache = [];
  }
  return _portfoliosCache;
}

// Esc-listener: один раз на module-init. router-induced remount'ы НЕ
// накопят слушателей (урок из task-inspector simplify-итерации).
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || _state.mode === 'closed') return;
  const t = document.activeElement;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  closeProjectInspector();
});

function buildDOM() {
  const root = document.createElement('aside');
  // Тот же класс что у task-inspector — стили (позиция, анимация, ширина)
  // переиспользуем 1-в-1. Дополнительный pi-only класс — для будущих
  // project-only стилей.
  root.className = 'task-inspector pi-inspector';
  root.hidden = true;
  root.innerHTML = `
    <header class="ti-header">
      <span class="ti-title">Проект</span>
      <button type="button" class="ti-close" title="Закрыть (Esc)" aria-label="Закрыть">✕</button>
    </header>
    <div class="ti-body"></div>
    <footer class="ti-footer"></footer>
  `;
  root.querySelector('.ti-close').addEventListener('click', closeProjectInspector);
  return root;
}

export function mountProjectInspector(container) {
  if (_root && !document.body.contains(_root)) {
    _root = null;
    _state = resetState();
  }
  if (_root) return _root;
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }
  _root = buildDOM();
  container.appendChild(_root);
  attachInspectorResize(_root, { key: 'project', defaultWidth: 380 });   // UI-2: ширина панели
  return _root;
}

export function openProjectInspector({ project, onAction, onClose }) {
  if (!_root) return;
  _state = {
    ...resetState(),
    mode:      'view',
    projectId: project.id,
    project,
    onAction:  onAction ?? (() => {}),
    onClose:   onClose  ?? (() => {}),
  };
  render();
  _root.hidden = false;
  void _root.offsetWidth;
  _root.classList.add('ti-open');
}

export function openCreateProjectInspector({ onAction, onClose }) {
  if (!_root) return;
  _state = {
    ...resetState(),
    mode:      'create',
    projectId: null,
    project: {
      name:          '',
      goal:          '',
      why_now:       '',
      hard_deadline: '',
      priority:      'P2',
      contour:       '',
    },
    onAction:  onAction ?? (() => {}),
    onClose:   onClose  ?? (() => {}),
  };
  render();
  _root.hidden = false;
  void _root.offsetWidth;
  _root.classList.add('ti-open');
  setTimeout(() => _root.querySelector('input[name="name"]')?.focus(), 30);
  // Подгружаем портфели для select — не блокируем render(). При успехе
  // дорисовываем select точечно (без полного renderCreate, чтобы не стереть
  // уже введённые Имя/Цель/Дедлайн).
  ensurePortfolios().then(list => {
    if (_state.mode !== 'create') return;
    _state.portfolios = list;
    repaintPortfolioSelect();
  });
}

/** Build a validated payload for POST /api/projects, or null on validation failure. */
export function getProjectInspectorCreatePayload() {
  if (_state.mode !== 'create') return null;
  const form = _root?.querySelector('.ti-body form');
  if (!form) return null;

  const name = (form.elements.name?.value ?? '').trim();
  const goal = (form.elements.goal?.value ?? '').trim();
  if (!name) {
    showProjectInspectorError('Имя не может быть пустым');
    return null;
  }
  if (!goal) {
    showProjectInspectorError('Заполните цель проекта');
    return null;
  }

  const fields = readForm(form);
  const payload = {
    name,
    goal,
    priority: fields.priority || 'P2',
  };
  if (_state.portfolioId != null) payload.portfolio_id = _state.portfolioId;
  if (fields.hard_deadline) payload.hard_deadline = fields.hard_deadline;
  if (fields.why_now)       payload.why_now       = fields.why_now;
  if (fields.contour)       payload.contour       = fields.contour;
  // Sub-plan C: ручной номер проекта (миграция 038). Опциональное поле,
  // backend проверит UNIQUE per tenant и вернёт 409 при collision.
  const projectCode = (fields.project_code ?? '').trim();
  if (projectCode) payload.project_code = projectCode;
  return payload;
}

/** Build payload {meta, files} для flow «Сформировать Диаграмму Гантта».
 *  Возвращает null если не все обязательные слоты заполнены либо name/goal пусты.
 */
export function getProjectInspectorBuildPayload() {
  if (_state.mode !== 'create') return null;
  const meta = getProjectInspectorCreatePayload();
  if (!meta) return null;
  for (const slot of DOC_SLOTS.filter(s => s.required)) {
    if (!_state.docs[slot.key]) {
      showProjectInspectorError(`Не загружен обязательный документ: ${slot.label}`);
      return null;
    }
  }
  const groups = new Set(DOC_SLOTS.map(s => s.requireGroup).filter(Boolean));
  for (const group of groups) {
    const slotsInGroup = DOC_SLOTS.filter(s => s.requireGroup === group);
    if (!slotsInGroup.some(s => _state.docs[s.key])) {
      const label = REQUIRE_GROUP_LABELS[group] ?? slotsInGroup.map(s => s.label).join(' или ');
      showProjectInspectorError(`Загрузите хотя бы один из: ${label}`);
      return null;
    }
  }
  const files = {};
  for (const [key, file] of Object.entries(_state.docs)) {
    if (file) files[key] = file;
  }
  return { meta, files };
}

export function closeProjectInspector() {
  if (!_root || _state.mode === 'closed') return;
  const cb = _state.onClose;
  _state = resetState();
  _root.classList.remove('ti-open');
  let done = false;
  const finalize = () => {
    if (done) return;
    done = true;
    _root.hidden = true;
    cb();
  };
  _root.addEventListener('transitionend', finalize, { once: true });
  setTimeout(finalize, 250);
}

export function getCurrentProjectId() {
  return _state.projectId;
}

/** Update the inspector body if it's currently showing this project. */
export function refreshInspectorProject(updatedProject) {
  if (_state.mode !== 'view' || _state.projectId !== updatedProject.id) return;
  _state.project = { ..._state.project, ...updatedProject };
  _state.errorMsg = null;
  render();
}

/** Return a diff object {field: newValue} of dirty fields, or null if nothing changed. */
export function getProjectInspectorDiff() {
  return collectDiff();
}

export function showProjectInspectorError(msg) {
  // F4 fix (2026-05-10): не делаем render() — он перезаписывает .ti-body
  // и теряет введённые в форме «Новый проект» значения (Имя/Цель/файлы
  // в 5 слотах). Обновляем DOM-блок .ti-error напрямую.
  _state.errorMsg = msg;
  const body = _root?.querySelector('.ti-body');
  if (!body) return;
  let errEl = body.querySelector('.ti-error');
  if (msg) {
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'ti-error';
      body.insertBefore(errEl, body.firstChild);
    }
    errEl.textContent = msg;
  } else if (errEl) {
    errEl.remove();
  }
}

function render() {
  if (_state.mode === 'create') return renderCreate();
  return renderView();
}

function renderView() {
  const { project, errorMsg } = _state;
  const fever = project.fever_zone ?? null;
  const progressPct = project.progress_pct != null ? Number(project.progress_pct) : null;
  const bufferPct   = project.buffer_consumed_pct != null ? Number(project.buffer_consumed_pct) : null;

  _root.querySelector('.ti-title').textContent = `Проект #${project.id}`;

  _root.querySelector('.ti-body').innerHTML = `
    ${errorMsg ? `<div class="ti-error">${escapeHtml(errorMsg)}</div>` : ''}
    <form class="ti-form" novalidate>
      <div class="ti-field">
        <label class="ti-label" for="pi-name">Имя</label>
        <input id="pi-name" name="name" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(project.name ?? '')}" required>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-code">Номер проекта</label>
        <input id="pi-code" name="project_code" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(project.project_code ?? '')}"
               placeholder="2026/14 или P-2026-007 (опционально)">
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <span class="ti-label">Статус</span>
          <span class="ti-value ti-status-${project.status}">
            ${feverEmoji(fever)} ${STATUS_RU[project.status] ?? project.status}
          </span>
        </div>
        <div class="ti-field">
          <span class="ti-label">Задач</span>
          <span class="ti-value">
            ${project.tasks_count ?? 0}
            ${project.in_progress_count ? `(в работе: ${project.in_progress_count})` : ''}
          </span>
        </div>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-goal">Цель *</label>
        <textarea id="pi-goal" name="goal" class="ti-input ti-textarea"
                  rows="2" required>${escapeHtml(project.goal ?? '')}</textarea>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-why">Why now</label>
        <textarea id="pi-why" name="why_now" class="ti-input ti-textarea"
                  rows="2">${escapeHtml(project.why_now ?? '')}</textarea>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label" for="pi-hard">Hard deadline</label>
          <input id="pi-hard" name="hard_deadline" class="ti-input"
                 type="date" value="${escapeAttr(project.hard_deadline ?? '')}">
        </div>
        <div class="ti-field">
          <label class="ti-label" for="pi-soft">Soft deadline</label>
          <input id="pi-soft" name="soft_deadline" class="ti-input"
                 type="date" value="${escapeAttr(project.soft_deadline ?? '')}">
        </div>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label" for="pi-priority">Приоритет</label>
          <select id="pi-priority" name="priority" class="ti-input">
            ${PRIORITIES.map(p => `
              <option value="${p.value}"${p.value === project.priority ? ' selected' : ''}>${escapeHtml(p.label)}</option>
            `).join('')}
          </select>
        </div>
        <div class="ti-field">
          <label class="ti-label" for="pi-contour">Контур</label>
          <input id="pi-contour" name="contour" class="ti-input"
                 type="text" value="${escapeAttr(project.contour ?? '')}">
        </div>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-portfolio">Портфель</label>
        <select id="pi-portfolio" name="portfolio_id" class="ti-input">
          <option value=""${project.portfolio_id == null ? ' selected' : ''}>— Без портфеля —</option>
          ${(_portfoliosCache ?? []).map(p => `
            <option value="${p.id}"${p.id === project.portfolio_id ? ' selected' : ''}>
              ${escapeHtml(p.name)}
            </option>`).join('')}
        </select>
      </div>
      ${renderActivationChecklist(project)}
      ${renderProgressBlock(progressPct, bufferPct, fever)}
      ${renderAssignmentsBlock()}
      ${renderReadOnlyBlock(project)}
    </form>
  `;

  wireViewForm();
  renderFooter(renderActions(project));
  // Lazy-load portfolios + assignments. Re-render when ready (only in view mode).
  hydrateLazyData(project.id);
}

// Ветка 3 (план 2026-05-13-wbs-3level-and-pipeline-ux.md): информационный
// чек-лист «Готовность к активации». Показываем только для queued —
// для других статусов смысла нет. Не блокирует кнопку «Активировать»:
// решение всё равно за владельцем (требование Руслана 2026-05-13).
function renderActivationChecklist(project) {
  if (project.status !== 'queued') return '';

  const tasksCount = Number(project.tasks_count ?? 0);
  const managers   = _state.assignments?.managers;
  const hasDeadline = !!(project.hard_deadline || project.soft_deadline);
  const hasGoal     = !!(project.goal && project.goal.trim());

  const items = [
    { ok: tasksCount > 0,  label: 'Задачи добавлены',
      hint: tasksCount > 0 ? `Задач: ${tasksCount}` : '+ Новая задача в шапке Gantt' },
    { ok: hasGoal,         label: 'Цель проекта заполнена',
      hint: hasGoal ? '' : 'Цель — что должен дать проект на выходе' },
    { ok: hasDeadline,     label: 'Hard или Soft deadline выставлен',
      hint: hasDeadline ? '' : 'Без дедлайна fever-zone не имеет смысла' },
    // Менеджеры подгружаются лениво — `null` означает «ещё не загружено»,
    // не «нет менеджеров». Показываем нейтральный «—» вместо ❌.
    { ok: managers == null ? null : managers.length > 0, label: 'Назначен Менеджер проекта',
      hint: managers == null ? '' : (managers.length > 0 ? '' : 'Phase 2: + Менеджер в блоке ниже') },
  ];

  return `
    <div class="ti-field pi-activation-checklist">
      <span class="ti-label">Готовность к активации</span>
      <ul class="pi-checklist">
        ${items.map(it => `
          <li class="pi-check-item${it.ok === true ? ' is-ok' : it.ok === false ? ' is-todo' : ' is-pending'}">
            <span class="pi-check-mark">${it.ok === true ? '✓' : it.ok === false ? '○' : '·'}</span>
            <span class="pi-check-label">${escapeHtml(it.label)}</span>
            ${it.hint ? `<span class="pi-check-hint">${escapeHtml(it.hint)}</span>` : ''}
          </li>`).join('')}
      </ul>
      <div class="pi-check-footnote">
        Чек-лист информационный — кнопка «Активировать» доступна всегда.
        Активация фиксирует baseline для отслеживания отклонений.
      </div>
    </div>
  `;
}

function renderAssignmentsBlock() {
  // Wave 4: показывать блок assignments только при наличии права назначать.
  if (!canDo('project.assign_manager')) return '';
  const m = _state.assignments?.managers ?? null;
  const v = _state.assignments?.viewers ?? null;
  if (m == null || v == null) {
    return `<div class="ti-field" data-assignments-slot>
      <span class="ti-label">Менеджеры / Пользователи</span>
      <span class="ti-value">Загрузка…</span>
    </div>`;
  }
  return `
    <div class="ti-field" data-assignments-slot>
      <span class="ti-label">Менеджеры (могут редактировать)</span>
      <div class="pi-assignees">
        ${m.length ? m.map(u => renderAssignmentChip(u, 'manager')).join('') : '<span class="ti-value">— нет —</span>'}
        <button type="button" class="ti-chip ti-chip-add" data-assignment-add="manager"
                title="Добавить Менеджера">+</button>
      </div>
    </div>
    <div class="ti-field">
      <span class="ti-label">Пользователи (только просмотр)</span>
      <div class="pi-assignees">
        ${v.length ? v.map(u => renderAssignmentChip(u, 'viewer')).join('') : '<span class="ti-value">— нет —</span>'}
        <button type="button" class="ti-chip ti-chip-add" data-assignment-add="viewer"
                title="Добавить Пользователя">+</button>
      </div>
    </div>`;
}

function renderAssignmentChip(u, kind) {
  return `
    <span class="ti-chip">
      ${escapeHtml(u.full_name)}
      <button type="button" class="ti-chip-x"
              data-assignment-remove="${kind}"
              data-user-id="${u.id}"
              title="Снять">✕</button>
    </span>`;
}

async function hydrateLazyData(projectId) {
  // Параллельно подгружаем portfolios + assignments. ТОЧЕЧНО обновляем DOM
  // вместо полного render() — иначе innerHTML формы перезаписывается во время
  // ввода юзером (открытый <select> закрывается, фокус теряется,
  // value сбрасывается). Bug: «при выборе портфеля меню выходит и сразу
  // складывается + во всех полях странное поведение».
  const portfoliosWasMissing = _portfoliosCache == null;
  const promises = [ensurePortfolios()];
  if (canDo('project.assign_manager')) {
    promises.push(
      Promise.all([
        api.projectManagers(projectId),
        api.projectViewers(projectId),
      ]).then(([managers, viewers]) => {
        if (_state.mode !== 'view' || _state.projectId !== projectId) return;
        _state.assignments = { managers, viewers };
      }).catch(() => {})
    );
  }
  await Promise.all(promises);

  if (_state.mode !== 'view' || _state.projectId !== projectId) return;

  // 1. Portfolio: дополняем <select> options, сохраняя текущий выбор.
  if (portfoliosWasMissing && _portfoliosCache?.length) {
    const sel = _root?.querySelector('#pi-portfolio');
    if (sel) {
      const currentValue = sel.value;
      const projPid = _state.project.portfolio_id;
      const optsHtml = [
        `<option value=""${projPid == null ? ' selected' : ''}>— Без портфеля —</option>`,
        ..._portfoliosCache.map(p =>
          `<option value="${p.id}"${p.id === projPid ? ' selected' : ''}>${escapeHtml(p.name)}</option>`
        ),
      ].join('');
      sel.innerHTML = optsHtml;
      // Если юзер уже что-то выбрал до hydrate — восстанавливаем.
      if (currentValue) sel.value = currentValue;
    }
  }

  // 2. Assignments: обновляем только два блока с chip'ами.
  const slot = _root?.querySelector('[data-assignments-slot]');
  if (slot && _state.assignments) {
    // Заменяем оба блока (slot + следующий sibling) на свежий HTML.
    // outerHTML переписывает только нужный кусок, не трогая form-input'ы выше.
    const tmp = document.createElement('div');
    tmp.innerHTML = renderAssignmentsBlock();
    const newBlocks = [...tmp.children];
    const oldBlocks = [slot, slot.nextElementSibling].filter(Boolean);
    oldBlocks.forEach((el, i) => {
      if (newBlocks[i]) el.replaceWith(newBlocks[i]);
      else el.remove();
    });
  }
}

function renderProgressBlock(progressPct, bufferPct, fever) {
  if (progressPct == null && bufferPct == null) return '';
  return `
    <div class="ti-field">
      <span class="ti-label">Прогресс / Буфер</span>
      <div class="ti-overrun-grid">
        ${progressPct != null ? `<div><span class="ti-overrun-key">Прогресс CC:</span> ${progressPct.toFixed(1)}%</div>` : ''}
        ${bufferPct   != null ? `<div><span class="ti-overrun-key">Буфер съеден:</span> ${bufferPct.toFixed(1)}%</div>` : ''}
        ${fever ? `<div class="ti-overrun-label">Fever-зона: ${feverEmoji(fever)} ${fever}</div>` : ''}
      </div>
    </div>
  `;
}

function renderReadOnlyBlock(project) {
  const startDate = project.start_date ? formatDate(project.start_date) : '—';
  const created   = project.created_at ? formatDate(project.created_at) : '—';
  return `
    <details class="ti-extra">
      <summary>Системные поля</summary>
      <div class="ti-extra-body">
        <div class="ti-row2">
          <div class="ti-field">
            <span class="ti-label">Старт</span>
            <span class="ti-value">${startDate}</span>
          </div>
          <div class="ti-field">
            <span class="ti-label">Создан</span>
            <span class="ti-value">${created}</span>
          </div>
        </div>
      </div>
    </details>
  `;
}

function renderCreate() {
  const { project, errorMsg } = _state;
  _root.querySelector('.ti-title').textContent = 'Новый проект';

  _root.querySelector('.ti-body').innerHTML = `
    ${errorMsg ? `<div class="ti-error">${escapeHtml(errorMsg)}</div>` : ''}
    <form class="ti-form" novalidate>
      <div class="ti-field">
        <label class="ti-label" for="pi-name">Имя *</label>
        <input id="pi-name" name="name" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(project.name ?? '')}" required>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-code">Номер проекта</label>
        <input id="pi-code" name="project_code" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(project.project_code ?? '')}"
               placeholder="Например 2026/14 или P-2026-007 (опционально)">
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-goal">Цель *</label>
        <textarea id="pi-goal" name="goal" class="ti-input ti-textarea"
                  rows="2" required
                  placeholder="Что должно произойти, чтобы проект считался сделанным?"
        >${escapeHtml(project.goal ?? '')}</textarea>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-why">Why now</label>
        <textarea id="pi-why" name="why_now" class="ti-input ti-textarea"
                  rows="2"
                  placeholder="Почему сейчас, а не позже?"
        >${escapeHtml(project.why_now ?? '')}</textarea>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label" for="pi-hard">Hard deadline</label>
          <input id="pi-hard" name="hard_deadline" class="ti-input"
                 type="date" value="${escapeAttr(project.hard_deadline ?? '')}">
        </div>
        <div class="ti-field">
          <label class="ti-label" for="pi-priority">Приоритет</label>
          <select id="pi-priority" name="priority" class="ti-input">
            ${PRIORITIES.map(p => `
              <option value="${p.value}"${p.value === (project.priority ?? 'P2') ? ' selected' : ''}>${escapeHtml(p.label)}</option>
            `).join('')}
          </select>
        </div>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-contour">Контур</label>
        <input id="pi-contour" name="contour" class="ti-input"
               type="text" value="${escapeAttr(project.contour ?? '')}">
      </div>
      <div class="ti-field">
        <label class="ti-label" for="pi-portfolio">Портфель</label>
        <select id="pi-portfolio" name="portfolio_id" class="ti-input" data-portfolio-select>
          ${renderPortfolioOptions()}
        </select>
        <p class="ti-hint-sm">Портфель определяет, кому проект виден. PRIVATE — только владельцу.
          Без портфеля — проект не привязан к группе.</p>
      </div>
      <fieldset class="ti-fieldset" id="pi-docs-fieldset">
        <legend class="ti-legend">Документы проекта</legend>
        <div class="pi-docs-list">
          ${DOC_SLOTS.map(slot => renderDocSlot(slot)).join('')}
        </div>
      </fieldset>
      <p class="ti-hint">Без документов — кнопка <b>«Создать пустой»</b>: проект в статусе <code>queued</code>,
        задачи добавите вручную. С загруженными ТЗ + Состав П <i>или</i> Состав Р —
        доступна кнопка <b>«Сформировать Диаграмму Гантта»</b>: assembler разберёт файлы и
        создаст задачи + связи + buffer автоматически. Контракт и Календарный план опциональны.</p>
    </form>
  `;

  wireCreateForm();

  renderFooter(renderCreateFooter());
}

function renderDocSlot(slot) {
  const file = _state.docs[slot.key];
  let reqMark = '';
  if (slot.required) {
    reqMark = ' <span class="pi-doc-req">*</span>';
  } else if (slot.requireGroup) {
    reqMark = ' <span class="pi-doc-req" title="хотя бы один из двух">*</span>';
  }
  return `
    <div class="pi-doc-slot${file ? ' has-file' : ''}" data-doc-type="${slot.key}">
      <span class="pi-doc-icon" aria-hidden="true">${iconFolder(20)}</span>
      <div class="pi-doc-info">
        <div class="pi-doc-title">${escapeHtml(slot.label)}${reqMark}</div>
        <div class="pi-doc-filename">${file ? escapeHtml(file.name) : 'нет файла'}</div>
      </div>
      <div class="pi-doc-actions">
        <label class="pi-doc-btn">
          <input type="file" accept=".docx" data-doc-input="${slot.key}" hidden>
          ${file ? 'Заменить' : 'Загрузить'}
        </label>
        ${file ? `<button type="button" class="pi-doc-remove" data-doc-remove="${slot.key}" title="Убрать файл">✕</button>` : ''}
      </div>
    </div>
  `;
}

function renderCreateFooter() {
  const canBuild = canBuildGantt();
  return `
    <button type="button" data-action="create" class="ti-btn ti-btn-primary">✓ Создать пустой</button>
    <button type="button" data-action="build-gantt" class="ti-btn ti-btn-primary"${canBuild ? '' : ' disabled'}
      title="${canBuild ? 'Собрать Gantt из загруженных docx' : 'Загрузите ТЗ и хотя бы один из Состав П / Состав Р'}">
      📊 Сформировать Диаграмму Гантта
    </button>
    <button type="button" data-action="cancel" class="ti-btn">Отмена</button>
  `;
}

/** Проверяет, можно ли запустить кнопку «Сформировать Диаграмму Гантта».
 *  Правила: (1) все слоты с required=true должны быть загружены;
 *  (2) в каждой OR-группе (requireGroup) хотя бы один файл. */
function canBuildGantt() {
  const visible = DOC_SLOTS.filter(s => !s.businessOnly || _state.isBusiness);
  if (!visible.filter(s => s.required).every(s => _state.docs[s.key])) return false;
  const groups = new Set(visible.map(s => s.requireGroup).filter(Boolean));
  return [...groups].every(g =>
    visible.some(s => s.requireGroup === g && _state.docs[s.key]),
  );
}

function wireViewForm() {
  const form = _root.querySelector('.ti-body form');
  form.addEventListener('input',  updateSaveButtonState);
  form.addEventListener('change', updateSaveButtonState);

  // Wave 2: assignment chips remove + add (only owner/admin).
  const body = _root.querySelector('.ti-body');
  body.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-assignment-remove]');
    if (removeBtn) {
      e.preventDefault();
      const kind = removeBtn.dataset.assignmentRemove;
      const userId = Number(removeBtn.dataset.userId);
      await onRemoveAssignment(kind, userId);
      return;
    }
    const addBtn = e.target.closest('[data-assignment-add]');
    if (addBtn) {
      e.preventDefault();
      const kind = addBtn.dataset.assignmentAdd;
      await onAddAssignment(kind, addBtn);
    }
  });
}

async function onRemoveAssignment(kind, userId) {
  if (!_state.projectId) return;
  try {
    if (kind === 'manager') await api.removeProjectManager(_state.projectId, userId);
    else                    await api.removeProjectViewer(_state.projectId, userId);
    _state.assignments = null;
    render();
    hydrateLazyData(_state.projectId);
  } catch (err) {
    showNotification(err.detail || err.message || 'Не удалось снять назначение', { type: 'error' });
  }
}

async function onAddAssignment(kind, anchorBtn) {
  if (!_state.projectId) return;
  let users;
  try {
    users = await api.users();
  } catch (err) {
    showNotification(err.detail || err.message || 'Ошибка загрузки списка users', { type: 'error' });
    return;
  }
  const expectedRole = kind === 'manager' ? 'manager' : 'user';
  const alreadyAssigned = new Set(
    (kind === 'manager' ? _state.assignments?.managers : _state.assignments?.viewers)?.map(u => u.id) ?? []
  );
  const candidates = users.filter(u => u.role === expectedRole && u.active && !alreadyAssigned.has(u.id));
  if (!candidates.length) {
    showNotification(`Нет доступных пользователей с ролью «${expectedRole}». Создайте через /admin.`, { type: 'info' });
    return;
  }
  _picker.open({
    anchor: anchorBtn,
    items: candidates,
    renderItem: (u) => `${escapeHtml(u.full_name)} <span class="ip-channel">(${escapeHtml(u.login)})</span>`,
    filterText: (u) => `${u.full_name} ${u.login}`,
    emptyMessage: `Нет доступных пользователей с ролью «${expectedRole}»`,
    placeholder: 'Поиск по имени или логину…',
    onPick: async (target) => {
      try {
        if (kind === 'manager') await api.addProjectManager(_state.projectId, target.id);
        else                    await api.addProjectViewer(_state.projectId, target.id);
        _state.assignments = null;
        render();
        hydrateLazyData(_state.projectId);
      } catch (err) {
        showNotification(err.detail || err.message || 'Не удалось добавить', { type: 'error' });
      }
    },
    onCancel: () => {},
  });
}

function wireCreateForm() {
  const form = _root.querySelector('.ti-body form');
  if (!form) return;

  // Select портфеля — обновляет только state, не дёргает re-render.
  // Имя/Цель/Дедлайн остаются как пользователь ввёл (они хранятся в DOM-inputs,
  // не в state — потеря была бы при renderCreate).
  const portfolioSelect = form.querySelector('[data-portfolio-select]');
  if (portfolioSelect) {
    portfolioSelect.addEventListener('change', (e) => {
      const val = e.target.value;
      _state.portfolioId = val === '' ? null : Number(val);
    });
  }

  // File-input на каждом слоте — точечный re-render одного слота + footer.
  form.querySelectorAll('input[data-doc-input]').forEach(input => {
    input.addEventListener('change', (e) => {
      const docType = e.target.dataset.docInput;
      const file = e.target.files[0] ?? null;
      if (file) {
        if (!file.name.toLowerCase().endsWith('.docx')) {
          showProjectInspectorError(`${file.name}: поддерживаются только .docx`);
          e.target.value = '';
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          showProjectInspectorError(`${file.name}: файл больше 10 MB`);
          e.target.value = '';
          return;
        }
        _state.docs[docType] = file;
        _state.errorMsg = null;
        repaintDocSlot(docType);
        renderFooter(renderCreateFooter());
      }
    });
  });

  // ✕-кнопка для удаления файла из слота — точечный re-render.
  form.querySelectorAll('[data-doc-remove]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const docType = e.currentTarget.dataset.docRemove;
      _state.docs[docType] = null;
      repaintDocSlot(docType);
      renderFooter(renderCreateFooter());
    });
  });
}

/** Возвращает HTML-options для select Портфель. */
function renderPortfolioOptions() {
  const sel = _state.portfolioId;
  const placeholder = `<option value=""${sel == null ? ' selected' : ''}>— без портфеля —</option>`;
  const items = (_state.portfolios ?? []).map(p =>
    `<option value="${p.id}"${p.id === sel ? ' selected' : ''}>${escapeHtml(p.name)}</option>`
  ).join('');
  return placeholder + items;
}

/** Перерисовывает только select портфеля, не трогая остальную форму. */
function repaintPortfolioSelect() {
  const select = _root?.querySelector('[data-portfolio-select]');
  if (!select) return;
  select.innerHTML = renderPortfolioOptions();
}

/** Точечный re-render одного слота документа (после upload / remove). */
function repaintDocSlot(docType) {
  const slot = DOC_SLOTS.find(s => s.key === docType);
  if (!slot) return;
  const old = _root?.querySelector(`.pi-doc-slot[data-doc-type="${docType}"]`);
  if (!old) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderDocSlot(slot).trim();
  const fresh = tmp.firstElementChild;
  old.replaceWith(fresh);
  // Пере-подвязать file-input + ✕ на новом узле (старые listener'ы ушли с replaceWith).
  const fileInput = fresh.querySelector('input[data-doc-input]');
  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0] ?? null;
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.docx')) {
        showProjectInspectorError(`${file.name}: поддерживаются только .docx`);
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showProjectInspectorError(`${file.name}: файл больше 10 MB`);
        e.target.value = '';
        return;
      }
      _state.docs[docType] = file;
      _state.errorMsg = null;
      repaintDocSlot(docType);
      renderFooter(renderCreateFooter());
    });
  }
  const removeBtn = fresh.querySelector('[data-doc-remove]');
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      _state.docs[docType] = null;
      repaintDocSlot(docType);
      renderFooter(renderCreateFooter());
    });
  }
}

function renderFooter(html) {
  const footer = _root.querySelector('.ti-footer');
  footer.innerHTML = html;
  footer.querySelectorAll('button[data-action]').forEach(btn => {
    btn.addEventListener('click', () => _state.onAction(btn.dataset.action));
  });
  updateSaveButtonState();
}

// State-machine из backend/app/api/projects.py — допустимые переходы.
// Кнопка «отменить» доступна из любого статуса кроме cancelled (POST /cancel
// принимает любой не-cancelled, см. cancel_project в api/projects.py:399).
function renderActions(project) {
  // Wave 4: показываем mutation-кнопки если есть хоть одно право (transition/cancel/update).
  // Backend всё равно режет 403, но UI чище без бесполезных кнопок.
  if (!canDo('project.update') && !canDo('project.transition') && !canDo('project.cancel')) {
    return '';
  }
  const status = project.status;
  const buttons = [saveBtnHtml()];
  if (status === 'queued') {
    buttons.push(btn('activate', '▶ Активировать', 'ti-btn-primary'));
  }
  if (status === 'active') {
    buttons.push(btn('pause',    '⏸ Поставить на паузу'));
    buttons.push(btn('complete', '✓ Завершить', 'ti-btn-primary'));
  }
  if (status === 'paused') {
    buttons.push(btn('resume', '▶ Возобновить', 'ti-btn-primary'));
  }
  if (status !== 'cancelled' && status !== 'done') {
    buttons.push(btn('cancel', '🗑 Отменить проект', 'ti-btn-danger'));
  }
  return buttons.join('');
}

const saveBtnHtml = () =>
  `<button type="button" data-action="save" class="ti-btn ti-btn-save" disabled>💾 Сохранить</button>`;

function btn(action, label, extraClass = '') {
  return `<button type="button" data-action="${action}" class="ti-btn ${extraClass}">${escapeHtml(label)}</button>`;
}

function readForm(form) {
  const out = {};
  for (const name of FIELDS_ALL) {
    const el = form.elements.namedItem(name);
    if (!el) continue;
    const v = (el.value ?? '').trim();
    if (name === 'portfolio_id') {
      out[name] = v === '' ? null : Number(v);
    } else {
      out[name] = v === '' ? null : v;
    }
  }
  return out;
}

function diffField(name, baseline, current) {
  const b = baseline?.[name];
  const c = current[name];
  const bs = b == null || b === '' ? null : String(b);
  const cs = c == null || c === '' ? null : String(c);
  return bs !== cs;
}

function collectDiff() {
  const form = _root?.querySelector('.ti-body form');
  if (!form) return null;
  const baseline = _state.project;
  const current  = readForm(form);
  const diff = {};
  for (const k of Object.keys(current)) {
    if (diffField(k, baseline, current)) diff[k] = current[k];
  }
  return Object.keys(diff).length ? diff : null;
}

function updateSaveButtonState() {
  if (!_root) return;
  const saveBtn = _root.querySelector('.ti-footer [data-action="save"]');
  if (!saveBtn) return;
  const dirty = !!collectDiff();
  saveBtn.disabled = !dirty;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function escapeAttr(s) {
  return escapeHtml(s);
}

function formatDate(iso) {
  // Принимает 'YYYY-MM-DD' (date) или ISO datetime — возвращает display 'DD-MM-YY'.
  // Используется только для отображения в шапке проекта (start_date, created_at).
  if (!iso) return '';
  const d = new Date(String(iso));
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const yy  = String(d.getFullYear()).slice(-2);
  return `${day}-${m}-${yy}`;
}
